'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { JobBlock, CrewMember } from './CalendarInteractive';

interface JobDetailModalProps {
  job: JobBlock;
  crew: CrewMember[];
  onClose: () => void;
}

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  'scheduled': { label: 'Scheduled', color: 'text-blue-700', bg: 'bg-blue-100' },
  'in-progress': { label: 'In Progress', color: 'text-green-700', bg: 'bg-green-100' },
  'completed': { label: 'Completed', color: 'text-gray-600', bg: 'bg-gray-100' },
  'delayed': { label: 'Delayed', color: 'text-amber-700', bg: 'bg-amber-100' },
  'cancelled': { label: 'Cancelled', color: 'text-red-700', bg: 'bg-red-100' },
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  high: { label: 'High Priority', color: 'text-error' },
  medium: { label: 'Medium Priority', color: 'text-warning' },
  low: { label: 'Low Priority', color: 'text-success' },
};

export default function JobDetailModal({ job, crew, onClose }: JobDetailModalProps) {
  const assignedCrew = crew.find(c => c.id === job.crewId);
  const status = statusConfig[job.status];
  const priority = priorityConfig[job.priority];

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-x-0 bottom-0 top-[60px] z-50 flex items-center justify-end" role="dialog" aria-modal="true" aria-label="Job Detail">
      <div className="absolute inset-0 bg-accent/40" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-card shadow-2xl flex flex-col overflow-hidden animate-slide-in-right">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-border bg-card flex-shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`font-caption text-xs px-2 py-0.5 rounded-full font-medium ${status.bg} ${status.color}`}>
                {status.label}
              </span>
              <span className={`font-caption text-xs font-medium ${priority.color}`}>
                {priority.label}
              </span>
            </div>
            <h2 className="font-heading font-semibold text-foreground text-lg">{job.title}</h2>
            <p className="font-caption text-sm text-muted-foreground">{job.customer} · {job.projectType}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-standard flex-shrink-0">
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Location */}
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
              <Icon name="MapPinIcon" size={15} className="text-primary" />
              Location
            </h3>
            <p className="font-body text-sm text-foreground">{job.address}</p>
            <div className="mt-2 h-32 rounded-md overflow-hidden border border-border">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title={`Map for ${job.address}`}
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=30.2672,-97.7431&z=14&output=embed"
              />
            </div>
          </div>

          {/* Schedule */}
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
              <Icon name="ClockIcon" size={15} className="text-primary" />
              Schedule
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-muted rounded-md p-2.5 text-center">
                <p className="font-caption text-xs text-muted-foreground">Date</p>
                <p className="font-body text-sm font-medium text-foreground mt-0.5">{job.date}</p>
              </div>
              <div className="bg-muted rounded-md p-2.5 text-center">
                <p className="font-caption text-xs text-muted-foreground">Start</p>
                <p className="font-body text-sm font-medium text-foreground mt-0.5">
                  {job.startHour < 12 ? `${job.startHour}:00 AM` : `${job.startHour - 12}:00 PM`}
                </p>
              </div>
              <div className="bg-muted rounded-md p-2.5 text-center">
                <p className="font-caption text-xs text-muted-foreground">Duration</p>
                <p className="font-body text-sm font-medium text-foreground mt-0.5">{job.duration}h</p>
              </div>
            </div>
          </div>

          {/* Crew */}
          {assignedCrew && (
            <div>
              <h3 className="font-heading text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                <Icon name="UsersIcon" size={15} className="text-primary" />
                Assigned Crew
              </h3>
              <div className="flex items-center gap-3 p-3 bg-muted rounded-md">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <AppImage
                    src={assignedCrew.avatar}
                    alt={`Profile photo of ${assignedCrew.name}, ${assignedCrew.role}`}
                    width={40}
                    height={40}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <p className="font-body text-sm font-medium text-foreground">{assignedCrew.name}</p>
                  <p className="font-caption text-xs text-muted-foreground">{assignedCrew.role} · {job.crew}</p>
                </div>
                <a
                  href={`tel:${assignedCrew.phone}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-body rounded-md hover:bg-secondary transition-standard"
                >
                  <Icon name="PhoneIcon" size={13} variant="solid" />
                  Call
                </a>
              </div>
            </div>
          )}

          {/* Financials */}
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
              <Icon name="BanknotesIcon" size={15} className="text-primary" />
              Financials
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted rounded-md p-2.5">
                <p className="font-caption text-xs text-muted-foreground">Labor Cost</p>
                <p className="font-data text-base font-semibold text-foreground mt-0.5">${job.laborCost.toLocaleString()}</p>
              </div>
              <div className="bg-muted rounded-md p-2.5">
                <p className="font-caption text-xs text-muted-foreground">Profit Margin</p>
                <p className="font-data text-base font-semibold text-success mt-0.5">{job.profitMargin}%</p>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
              <Icon name="DocumentTextIcon" size={15} className="text-primary" />
              Notes
            </h3>
            <p className="font-body text-sm text-foreground bg-muted rounded-md p-3 leading-relaxed">{job.notes}</p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 px-5 py-4 border-t border-border bg-card space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Link
              href="/job-detail-management-panel"
              className="flex items-center justify-center gap-1.5 py-2.5 bg-secondary text-white text-sm font-body font-medium rounded-md hover:bg-accent transition-standard"
            >
              <Icon name="DocumentPlusIcon" size={15} variant="solid" />
              Change Order
            </Link>
            <Link
              href="/photo-gallery-approval-workflow-system"
              className="flex items-center justify-center gap-1.5 py-2.5 bg-muted text-foreground text-sm font-body font-medium rounded-md hover:bg-border transition-standard"
            >
              <Icon name="PhotoIcon" size={15} />
              View Photos
            </Link>
          </div>
          <Link
            href="/job-detail-management-panel"
            className="flex items-center justify-center gap-1.5 w-full py-2.5 bg-primary text-white text-sm font-body font-medium rounded-md hover:bg-secondary transition-standard"
          >
            <Icon name="ArrowTopRightOnSquareIcon" size={15} variant="solid" />
            Open Full Job Detail
          </Link>
        </div>
      </div>
    </div>
  );
}